import React, { useRef, useCallback, useState, useEffect, createContext, useContext }from 'react';
import { createPortal } from 'react-dom';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { openFindingForContext, setFindingContextFromGridRow } from '@process-advisor/process-miner/shared/findingContext';
import { AgGridReact } from 'ag-grid-react';
import '@process-advisor/process-miner/dashboard/shared/evidenceGridStyles.css';
import { AgCharts } from "ag-charts-react";
import { themeAlpine } from 'ag-grid-community';
import { AppButton as Button } from '@advisor-core/components/AppButton';
import MoreIcon from '@atlaskit/icon/glyph/more';

import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';

// Create context for menu state
const MenuContext = createContext(null);

export const DataGrid = ({rows,graphData,columns}) => {
    const grid = useRef(null);
    const sleep = ms => new Promise(r => setTimeout(r, ms));


    const [openMenuRowId, setOpenMenuRowId] = useState(null);

    // Force AG-Grid to refresh cells when menu state changes
    useEffect(() => {
        if (grid.current && grid.current.api) {
            grid.current.api.refreshCells({
                columns: ['actions'],
                force: true
            });
        }
    }, [openMenuRowId]);

    // Helper function to execute menu actions
    const executeMenuAction = async (action, data) => {
        setOpenMenuRowId(null); // Close menu
        await action(data);
    };

    // Get menu items structure
    const getMenuItems = (data) => {
        return {
            insights: [
                {
                    name: 'Clusters',
                    action: async () => {
                        openProgressModal({status: 'Processing Clusters'});
                        await sleep(200);
                        await apiProvider.generateTasksFilteredArray({
                            variantPaneVariants: false,
                            type: 'step',
                            variant: 0,
                            activity: 0,
                            from: data.from_activity_id,
                            to: data.to_activity_id
                        });
                        apiProvider.setDiagramCluster({source: 'step'});
                        closeProgressModal();
                    }
                },
                {
                    name: 'Influencers',
                    action: async () => {
                        openProgressModal({status: 'Processing Influencer Insights'});
                        await sleep(200);
                        await apiProvider.generateTasksFilteredArray({
                            variantPaneVariants: false,
                            type: 'step',
                            variant: 0,
                            activity: 0,
                            from: data.from_activity_id,
                            to: data.to_activity_id
                        });
                        apiProvider.setDiagramInfluenceAnalysis({source: 'step'});
                        closeProgressModal();
                    }
                },
                {
                    name: 'Decompose',
                    action: () => {
                        apiProvider.generateTasksFilteredArray({
                            variantPaneVariants: false,
                            type: 'step',
                            variant: 0,
                            activity: 0,
                            from: data.from_activity_id,
                            to: data.to_activity_id
                        });
                        apiProvider.setDiagramDecompositionTree({source: 'step'});
                    }
                },
                {
                    name: 'Activity Analysis',
                    action: () => {
                        apiProvider.generateTasksFilteredArray({
                            variantPaneVariants: false,
                            type: 'step',
                            variant: 0,
                            activity: 0,
                            from: data.from_activity_id,
                            to: data.to_activity_id
                        });
                        apiProvider.setTimeInActivitySource({source: 'step'});
                        apiProvider.setAction_Time({});
                    }
                },
                {
                    name: 'Histogram',
                    action: () => {
                        apiProvider.generateHistogramArray({
                            variantPaneVariants: false,
                            type: 'step-tasks',
                            variant: 0,
                            activity: 0,
                            from: data.from_activity_id,
                            to: data.to_activity_id
                        });
                        apiProvider.histogram({});
                    }
                }
            ],
            workItems: [
                {
                    name: 'Detail',
                    action: () => {
                        apiProvider.setStepDetailProps({
                            from: data.from_activity_id,
                            to: data.to_activity_id
                        });
                        apiProvider.taskDetail({});
                    }
                },
                {
                    name: 'History',
                    action: () => {
                        apiProvider.processAnalytics({
                            from: data.from_activity_id,
                            to: data.to_activity_id,
                            object_type: 'Step',
                            object_name: data.step,
                            series_name: apiProvider.getEntityUpper() + 's'
                        });
                    }
                },
                {
                    name: 'LifeCycle',
                    action: () => {
                        apiProvider.generateTasksFilteredArray({
                            variantPaneVariants: false,
                            type: 'step',
                            variant: 0,
                            activity: 0,
                            from: data.from_activity_id,
                            to: data.to_activity_id
                        });
                        apiProvider.setTasksVisualizationSource({source: 'step'});
                        apiProvider.tasksVisualization();
                        apiProvider.taskLifecycle({});
                    }
                }
            ],
            events: [
                {
                    name: 'Detail',
                    action: () => {
                        apiProvider.setStepDetailProps({
                            from: data.from_activity_id,
                            to: data.to_activity_id
                        });
                        apiProvider.eventDetail({});
                    }
                },
                {
                    name: 'Histogram',
                    action: () => {
                        apiProvider.generateHistogramArray({
                            variantPaneVariants: false,
                            type: 'step-events',
                            variant: 0,
                            activity: 0,
                            from: data.from_activity_id,
                            to: data.to_activity_id
                        });
                        apiProvider.histogram({});
                    }
                }
            ],
            export: [
                {
                    name: 'CSV Export (all)',
                    action: () => grid.current.api.exportDataAsCsv({onlySelected: false})
                },
                {
                    name: 'CSV Export (selected)',
                    action: () => grid.current.api.exportDataAsCsv({onlySelected: true})
                },
                {
                    name: 'XLS Export (all)',
                    action: () => grid.current.api.exportDataAsExcel({onlySelected: false})
                },
                {
                    name: 'XLS Export (selected)',
                    action: () => grid.current.api.exportDataAsExcel({onlySelected: true})
                }
            ]
        };
    };

    // Actions menu renderer with Portal-based menu
    const ActionsMenuRenderer = (params) => {
        const { data } = params;
        
        if (data === undefined) {
            return '';
        }

        const menuContext = useContext(MenuContext);
        const openMenuRowId = menuContext?.openMenuRowId;
        const setOpenMenuRowId = menuContext?.setOpenMenuRowId;

        const [showSubMenu, setShowSubMenu] = useState(null);
        const [buttonPosition, setButtonPosition] = useState(null);
        const menuRef = useRef(null);
        const buttonRef = useRef(null);

        const menuItems = getMenuItems(data);
        
        // Use a combination of from and to activity IDs as unique row ID
        const rowId = `${data.from_activity_id}_${data.to_activity_id}`;
        const isOpen = openMenuRowId === rowId;

        // Update button position when menu opens
        useEffect(() => {
            if (isOpen && buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setButtonPosition({
                    top: Math.min(rect.bottom + 4, window.innerHeight - 260),
                    left: Math.max(8, Math.min(rect.right - 200, window.innerWidth - 208)),
                });
            }
        }, [isOpen]);

        // Close menu when clicking outside
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (isOpen && menuRef.current && !menuRef.current.contains(event.target) &&
                    buttonRef.current && !buttonRef.current.contains(event.target)) {
                    setOpenMenuRowId(null);
                    setShowSubMenu(null);
                }
            };
            
            if (isOpen) {
                document.addEventListener('mousedown', handleClickOutside);
                return () => document.removeEventListener('mousedown', handleClickOutside);
            }
        }, [isOpen, setOpenMenuRowId]);

        const handleButtonClick = () => {
            if (isOpen) {
                setOpenMenuRowId(null);
            } else {
                setOpenMenuRowId(rowId);
            }
        };

        return (
            <>
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div ref={buttonRef}>
                        <Button
                            startIcon={<MoreIcon label="Actions" />}
                            onClick={handleButtonClick}
                        />
                    </div>
                </div>

                {isOpen && buttonPosition && createPortal(
                    <div
                        ref={menuRef}
                        style={{
                            position: 'fixed',
                            top: `${buttonPosition.top}px`,
                            left: `${buttonPosition.left}px`,
                            backgroundColor: 'white',
                            border: '1px solid #DFE1E6',
                            borderRadius: '3px',
                            boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                            zIndex: 10000,
                            minWidth: '200px',
                            marginTop: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Insights submenu */}
                        <div
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#172B4D',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#F4F5F7';
                                setShowSubMenu('insights');
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <span>Insights</span>
                            <span style={{ fontSize: '12px', color: '#5E6C84' }}>›</span>
                            
                            {showSubMenu === 'insights' && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: '100%',
                                        top: '0',
                                        backgroundColor: 'white',
                                        border: '1px solid #DFE1E6',
                                        borderRadius: '3px',
                                        boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                                        minWidth: '180px',
                                        marginRight: '4px',
                                        zIndex: 10001
                                    }}
                                    onMouseEnter={() => setShowSubMenu('insights')}
                                    onMouseLeave={() => setShowSubMenu(null)}
                                >
                                    {menuItems.insights.map((item, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => {
                                                setOpenMenuRowId(null);
                                                executeMenuAction(item.action, data);
                                            }}
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                color: '#172B4D'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F5F7'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            {item.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Entity submenu */}
                        <div
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#172B4D',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#F4F5F7';
                                setShowSubMenu('workItems');
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <span>{apiProvider.getEntityUpper()}s</span>
                            <span style={{ fontSize: '12px', color: '#5E6C84' }}>›</span>
                            
                            {showSubMenu === 'workItems' && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: '100%',
                                        top: '0',
                                        backgroundColor: 'white',
                                        border: '1px solid #DFE1E6',
                                        borderRadius: '3px',
                                        boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                                        minWidth: '180px',
                                        marginRight: '4px',
                                        zIndex: 10001
                                    }}
                                    onMouseEnter={() => setShowSubMenu('workItems')}
                                    onMouseLeave={() => setShowSubMenu(null)}
                                >
                                    {menuItems.workItems.map((item, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => {
                                                setOpenMenuRowId(null);
                                                executeMenuAction(item.action, data);
                                            }}
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                color: '#172B4D'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F5F7'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            {item.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Events submenu */}
                        <div
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#172B4D',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#F4F5F7';
                                setShowSubMenu('events');
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <span>Events</span>
                            <span style={{ fontSize: '12px', color: '#5E6C84' }}>›</span>
                            
                            {showSubMenu === 'events' && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: '100%',
                                        top: '0',
                                        backgroundColor: 'white',
                                        border: '1px solid #DFE1E6',
                                        borderRadius: '3px',
                                        boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                                        minWidth: '180px',
                                        marginRight: '4px',
                                        zIndex: 10001
                                    }}
                                    onMouseEnter={() => setShowSubMenu('events')}
                                    onMouseLeave={() => setShowSubMenu(null)}
                                >
                                    {menuItems.events.map((item, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => {
                                                setOpenMenuRowId(null);
                                                executeMenuAction(item.action, data);
                                            }}
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                color: '#172B4D'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F5F7'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            {item.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Separator */}
                        <div style={{ 
                            height: '1px', 
                            backgroundColor: '#DFE1E6', 
                            margin: '4px 0' 
                        }} />

                        {/* Export submenu */}
                        <div
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#172B4D',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#F4F5F7';
                                setShowSubMenu('export');
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <span>Export</span>
                            <span style={{ fontSize: '12px', color: '#5E6C84' }}>›</span>
                            
                            {showSubMenu === 'export' && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: '100%',
                                        top: '0',
                                        backgroundColor: 'white',
                                        border: '1px solid #DFE1E6',
                                        borderRadius: '3px',
                                        boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                                        minWidth: '200px',
                                        marginRight: '4px',
                                        zIndex: 10001
                                    }}
                                    onMouseEnter={() => setShowSubMenu('export')}
                                    onMouseLeave={() => setShowSubMenu(null)}
                                >
                                    {menuItems.export.map((item, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => {
                                                setOpenMenuRowId(null);
                                                executeMenuAction(item.action, data);
                                            }}
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                color: '#172B4D'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F5F7'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            {item.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>,
                    document.body
                )}
            </>
        );
    };

    // Use AG Grid’s standard context menu; row-specific functions remain in Actions.


    const onSelectionChanged = (params) => {
        apiProvider.setStepFilterSelectedData({
            selection: params.api.getSelectedRows(),
        });
    };

    // Add Actions column to the columns array
    const columnsWithActions = [
        ...columns,
        {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ActionsMenuRenderer,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMenu: true,
            suppressColumnsToolPanel: true,
            suppressFiltersToolPanel: true,
            suppressNavigable: true,
            suppressCellFocus: true,
            editable: false,
            width: 80,
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0',
                overflow: 'visible'
            },
            cellClass: 'actions-cell',
            headerClass: 'actions-header'
        }
    ];

    const onRowClickedFindingContext = (params) => {
        setFindingContextFromGridRow(params?.data || params?.node?.data || {}, 'Evidence - Transitions', 'Step / Transition');
    };

    return (
        <MenuContext.Provider value={{ openMenuRowId, setOpenMenuRowId }}>
        <div className="evidence-ag-grid" style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
          backgroundColor: '#F4F5F7',
        }}>
          <div 
            style={{ 
                height: '600px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',  // Add this line
            }}
          >
            <style jsx>{`
              .ag-cell.actions-cell {
                overflow: visible !important;
                position: relative !important;
              }
              .ag-cell.actions-cell * {
                pointer-events: auto !important;
              }
              .ag-cell.actions-cell button {
                pointer-events: auto !important;
                cursor: pointer !important;
              }
              .ag-header-cell.actions-header .ag-header-cell-menu-button {
                display: none !important;
              }
            `}</style>
            
            <div style={{ height: '50%', minHeight: 0}}>
              <AgGridReact
          onRowClicked={onRowClickedFindingContext} theme={themeAlpine} 
                      rowSelection="single"
                      onSelectionChanged={onSelectionChanged}
                                  columnDefs={columnsWithActions}
                      headerHeight={48}
                      rowHeight={40}
                      ref={grid}
                      defaultColDef={{
                          sortable: true,
                          filter: true,
                          resizable: true,
                          wrapText: true,
                          autoHeight: true,
                      }}
                      animateRows={true}
                      rowData={rows} />
            </div>
            
            <div style={{ height: '50%', minHeight: 0 }}>
              <AgCharts options={{
                      data: graphData,
                      series: [{
                          type: "bar",
                          direction: "horizontal",
                          xKey: "step",
                          yKey: "work_items",
                          fill: '#1868DB'
                      }],
                      axes: [{
                          type: "category",
                          position: "left",
                      },
                      {
                          type: "number",
                          position: "bottom",
                      }
                      ],
                  }} />
            </div>
          </div>
         
        </div>
        </MenuContext.Provider>
    );
};